import React, { createContext, useContext, useState, useEffect } from "react";
import { useUser } from "./UserContext";
import { ref, set, get, remove, update } from "firebase/database";
import { database } from "../firebase";
import {
  createUserProfile,
  validateUserProfile,
  updateUserProfile,
  updateGameProgress as updateGameProgressModel,
} from "../models/UserProfile";

const UserProfileContext = createContext();

export const UserProfileProvider = ({ children }) => {
  const { user } = useUser();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Set loading state based on user authentication
  useEffect(() => {
    if (user?.uid) {
      // If we have a user but no profiles loaded yet, we should be loading
      setIsLoading(true);
    } else {
      // If no user, we're not loading
      setIsLoading(false);
    }
  }, [user?.uid]);

  // Load from cache immediately when user is available
  useEffect(() => {
    if (user?.uid) {
      try {
        const cachedProfiles = localStorage.getItem(`profiles_${user.uid}`);
        const cachedActiveProfile = localStorage.getItem(
          `activeProfile_${user.uid}`
        );

        if (cachedProfiles) {
          const parsedProfiles = JSON.parse(cachedProfiles);
          setProfiles(parsedProfiles);
        }

        if (cachedActiveProfile) {
          const parsedActiveProfile = JSON.parse(cachedActiveProfile);
          setActiveProfile(parsedActiveProfile);
        }
      } catch (error) {
        console.log("Error loading cached profiles:", error);
      }
    } else {
      // Clear profiles and active profile when no user
      setProfiles([]);
      setActiveProfile(null);
      setIsLoading(false);
    }
  }, [user?.uid]);

  // Load profiles from Firebase initially
  useEffect(() => {
    const loadProfiles = async () => {
      if (user?.uid) {
        setIsLoading(true);
        try {
          const profilesRef = ref(database, `profiles/${user.uid}`);
          const activeProfileRef = ref(database, `activeProfiles/${user.uid}`);

          const [profilesSnapshot, activeProfileSnapshot] = await Promise.all([
            get(profilesRef),
            get(activeProfileRef),
          ]);

          if (profilesSnapshot.exists()) {
            const profilesData = profilesSnapshot.val();
            const profilesList = Object.values(profilesData);

            // Validate each profile
            const validProfiles = profilesList.filter((profile) => {
              const { isValid, errors } = validateUserProfile(profile);
              if (!isValid) {
                console.warn(`Invalid profile found:`, errors);
                return false;
              }
              return true;
            });

            setProfiles(validProfiles);
            // Cache profiles in localStorage
            localStorage.setItem(
              `profiles_${user.uid}`,
              JSON.stringify(validProfiles)
            );

            // If there's an active profile, set it
            if (activeProfileSnapshot.exists()) {
              const activeProfileId = activeProfileSnapshot.val();
              const activeProfile = validProfiles.find(
                (p) => p.id === activeProfileId
              );
              if (activeProfile) {
                setActiveProfile(activeProfile);
                // Cache active profile in localStorage
                localStorage.setItem(
                  `activeProfile_${user.uid}`,
                  JSON.stringify(activeProfile)
                );
              }
            }
          } else {
            setProfiles([]);
            setActiveProfile(null);
            // Clear cache
            localStorage.removeItem(`profiles_${user?.uid}`);
            localStorage.removeItem(`activeProfile_${user?.uid}`);
          }
        } catch (error) {
          console.error("Error loading profiles:", error);
          // Even on error, make sure to set loading to false
          setProfiles([]);
          setActiveProfile(null);
        } finally {
          setIsLoading(false);
        }
      } else {
        // If no user, immediately set loading to false and clear profiles
        setIsLoading(false);
        setProfiles([]);
        setActiveProfile(null);
        // Clear any cached data
        if (typeof window !== "undefined") {
          Object.keys(localStorage).forEach((key) => {
            if (
              key.startsWith("profiles_") ||
              key.startsWith("activeProfile_")
            ) {
              localStorage.removeItem(key);
            }
          });
        }
      }
    };

    loadProfiles();
  }, [user]);

  const createProfile = async (profileData) => {
    if (!user?.uid) return null;

    const newProfile = createUserProfile(user.uid, profileData);
    const { isValid, errors } = validateUserProfile(newProfile);

    if (!isValid) {
      console.error("Invalid profile data:", errors);
      return null;
    }

    try {
      await set(
        ref(database, `profiles/${user.uid}/${newProfile.id}`),
        newProfile
      );

      const updatedProfiles = [...profiles, newProfile];
      setProfiles(updatedProfiles);
      localStorage.setItem(
        `profiles_${user.uid}`,
        JSON.stringify(updatedProfiles)
      );

      if (!activeProfile) {
        await set(ref(database, `activeProfiles/${user.uid}`), newProfile.id);
        setActiveProfile(newProfile);
        localStorage.setItem(
          `activeProfile_${user.uid}`,
          JSON.stringify(newProfile)
        );
      }

      return newProfile;
    } catch (error) {
      console.error("Error creating profile:", error);
      return null;
    }
  };

  const updateProfile = async (profileId, updates) => {
    if (!user?.uid) return;

    const currentProfile = profiles.find((p) => p.id === profileId);
    if (!currentProfile) return;

    const updatedProfile = updateUserProfile(currentProfile, updates);
    const { isValid, errors } = validateUserProfile(updatedProfile);

    if (!isValid) {
      console.error("Invalid profile update:", errors);
      return;
    }

    try {
      await update(
        ref(database, `profiles/${user.uid}/${profileId}`),
        updatedProfile
      );

      const updatedProfiles = profiles.map((profile) =>
        profile.id === profileId ? updatedProfile : profile
      );
      setProfiles(updatedProfiles);
      localStorage.setItem(
        `profiles_${user.uid}`,
        JSON.stringify(updatedProfiles)
      );

      if (activeProfile?.id === profileId) {
        setActiveProfile(updatedProfile);
        localStorage.setItem(
          `activeProfile_${user.uid}`,
          JSON.stringify(updatedProfile)
        );
      }
    } catch (error) {
      console.error("Error updating profile:", error);
    }
  };

  const deleteProfile = async (profileId) => {
    if (!user?.uid) return;

    try {
      await remove(ref(database, `profiles/${user.uid}/${profileId}`));

      setProfiles((prev) => prev.filter((profile) => profile.id !== profileId));

      if (activeProfile?.id === profileId) {
        const nextProfile = profiles.find((p) => p.id !== profileId);
        if (nextProfile) {
          await set(
            ref(database, `activeProfiles/${user.uid}`),
            nextProfile.id
          );
          setActiveProfile(nextProfile);
        } else {
          await remove(ref(database, `activeProfiles/${user.uid}`));
          setActiveProfile(null);
        }
      }
    } catch (error) {
      console.error("Error deleting profile:", error);
    }
  };

  const switchProfile = async (profileId) => {
    if (!user?.uid) return;

    const profile = profiles.find((p) => p.id === profileId);
    if (profile) {
      try {
        await set(ref(database, `activeProfiles/${user.uid}`), profileId);
        setActiveProfile(profile);
        localStorage.setItem(
          `activeProfile_${user.uid}`,
          JSON.stringify(profile)
        );
      } catch (error) {
        console.error("Error switching profile:", error);
      }
    }
  };

  const updateProfileSettings = async (profileId, settings) => {
    if (!user?.uid) return;

    const currentProfile = profiles.find((p) => p.id === profileId);
    if (!currentProfile) return;

    const updatedProfile = updateUserProfile(currentProfile, { settings });
    const { isValid, errors } = validateUserProfile(updatedProfile);

    if (!isValid) {
      console.error("Invalid settings update:", errors);
      return;
    }

    try {
      await update(
        ref(database, `profiles/${user.uid}/${profileId}/settings`),
        settings
      );

      setProfiles((prev) =>
        prev.map((profile) =>
          profile.id === profileId ? updatedProfile : profile
        )
      );

      if (activeProfile?.id === profileId) {
        setActiveProfile(updatedProfile);
      }
    } catch (error) {
      console.error("Error updating profile settings:", error);
    }
  };

  const updateGameProgress = async (profileId, gameId, progress) => {
    if (!user?.uid) return;

    const currentProfile = profiles.find((p) => p.id === profileId);
    if (!currentProfile) return;

    const updatedProfile = updateGameProgressModel(
      currentProfile,
      gameId,
      progress
    );
    const { isValid, errors } = validateUserProfile(updatedProfile);

    if (!isValid) {
      console.error("Invalid game progress update:", errors);
      return;
    }

    try {
      await update(
        ref(
          database,
          `profiles/${user.uid}/${profileId}/gameProgress/${gameId}`
        ),
        updatedProfile.gameProgress[gameId]
      );

      setProfiles((prev) =>
        prev.map((profile) =>
          profile.id === profileId ? updatedProfile : profile
        )
      );
    } catch (error) {
      console.error("Error updating game progress:", error);
    }
  };

  return (
    <UserProfileContext.Provider
      value={{
        profiles,
        activeProfile,
        isLoading,
        createProfile,
        updateProfile,
        deleteProfile,
        switchProfile,
        updateProfileSettings,
        updateGameProgress,
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfiles = () => {
  const context = useContext(UserProfileContext);
  if (!context) {
    throw new Error(
      "useUserProfiles must be used within a UserProfileProvider"
    );
  }
  return context;
};

export default UserProfileProvider;
